import React from 'react';
import styled from 'styled-components';
import { variant } from 'styled-system';
import { cover, transparentize } from 'polished';
import { object } from 'prop-types';
import { errorMessageOf } from '../../elements/formHook/errors';
import { ROLES } from '../../../constants';
import { isNotEmpty } from '../../../utils/validator';
import { useTranslate } from '../../../translate/I18n';
import { emitEvent, EVENTS } from '../../../utils/analytics';

import Box from '../../atoms/Box';
import LabelMessage from '../../atoms/LabelMessage';
import Check from '../../atoms/icons/Check';
import User from '../../atoms/icons/User';
import Suitcase from '../../atoms/icons/Suitcase';

const StyledLabel = styled('label')(({ theme }) => ({
  width: 'calc(50% - 12px)',
  display: 'inline-flex',
  marginRight: '12px',
  '&:nth-of-type(2)': {
    marginRight: 0,
    marginLeft: '12px',
    [`@media (max-width: ${theme.breakpoints.md})`]: {
      marginLeft: 0
    }
  },
  [`@media (max-width: ${theme.breakpoints.md})`]: {
    width: '100%',
    display: 'block',
    marginRight: 0,
    marginBottom: theme.space[3]
  }
}));

const AnimatedLabel = styled('div')(() => ({
  transition: 'transform .3s cubic-bezier(0.165, 0.84, 0.44, 1)',
  fontWeight: 700,
  transform: 'translateY(0)'
}));

const AnimatedDescription = styled('span')(() => ({
  transition: 'opacity .3s ease',
  display: 'inline-block',
  opacity: 1
}));

const HiddenRadio = styled('input')(({ theme }) => ({
  border: 0,
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: '1px',
  margin: '-1px',
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: '1px',
  '&:focus + div': { color: theme.colors.blue },
  '& + div:before': {
    transition: 'all .3s ease',
    content: '""',
    borderRadius: theme.radii[4],
    background: 'transparent',
    ...cover()
  },
  '&:focus + div:before': {
    background: transparentize(0.95, theme.colors.blue)
  },
  [`&:checked + ${StyledSpan}`]: {
    padding: '18px'
  },
  [`&:checked + ${StyledSpan}`]: {
    background: theme.colors.white,
    boxShadow: `0 0 0 3px ${theme.colors.blue} inset`
  },
  [`&:checked + ${StyledSpan} ${StyledIconContainer}`]: {
    opacity: 0,
    transform: 'scale(0.8)'
  },
  [`&:checked + ${StyledSpan} ${StyledCheckIcon}`]: {
    transform: 'scale(1)'
  },
  [`&:checked + ${StyledSpan} ${AnimatedLabel}`]: {
    transform: 'translateY(10px)'
  },
  [`&:checked + ${StyledSpan} ${AnimatedDescription}`]: {
    opacity: 0
  }
}));

const StyledIconContainer = styled('div')(() => ({
  fontSize: '32px',
  transition: 'all .3s ease',
  opacity: 1,
  transform: 'scale(1)'
}));

const StyledCheckIcon = styled(Check)(() => ({
  transition: 'transform .3s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
  fontSize: '32px',
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  transform: 'scale(0)'
}));

const StyledSpan = styled('span')(
  ({ theme }) => ({
    transition: 'all .3s ease',
    display: 'inline-flex',
    alignItems: 'center',
    borderRadius: theme.radii[4],
    padding: '16px 20px',
    background: theme.colors.greyVerylight,
    boxShadow: `0 0 0 ${0} ${theme.colors.blue} inset`,
    width: '100%',
    cursor: 'pointer'
  }),
  ({ theme }) =>
    variant({
      prop: 'state',
      variants: {
        error: {
          boxShadow: `0 0 0 2px ${theme.colors.red} inset`,
          backgroundColor: transparentize(0.9, theme.colors.red),
          '&:focus': {
            boxShadow: `0 0 0 2px ${theme.colors.red} inset`
          }
        }
      }
    })
);

const { FREELANCE, COMPANY } = ROLES;

const Role = ({ formHook }) => {
  const { t } = useTranslate(['validation', 'common']);
  const choices = [
    {
      label: t('common:imFreelance'),
      description: t('common:imFreelanceDesc'),
      icon: <User />,
      value: FREELANCE
    },
    {
      label: t('common:imCompany'),
      description: t('common:imCompanyDesc'),
      icon: <Suitcase />,
      value: COMPANY
    }
  ];
  const errorMessage = errorMessageOf(t, 'role', formHook.errors);
  const state = errorMessage ? 'error' : 'default';
  return (
    <Box mb={3}>
      {choices.map((choice, i) => {
        return (
          <StyledLabel
            key={i}
            onClick={() => emitEvent(EVENTS.REGISTER[choice.value])}>
            <HiddenRadio
              data-cy={`button-register-${choice.value}LookingFor`}
              ref={formHook.register({
                validate: {
                  selectRole: isNotEmpty
                }
              })}
              name="role"
              type="radio"
              value={choice.value}
            />
            <StyledSpan state={state}>
              <Box position={'relative'} mr={2}>
                <StyledIconContainer>{choice.icon}</StyledIconContainer>
                <StyledCheckIcon color="blue" />
              </Box>
              <Box>
                <AnimatedLabel>{choice.label}</AnimatedLabel>
                <AnimatedDescription>{choice.description}</AnimatedDescription>
              </Box>
            </StyledSpan>
          </StyledLabel>
        );
      })}
      {errorMessage && (
        <LabelMessage type="error" withIcon={true} inputName="role">
          {errorMessage}
        </LabelMessage>
      )}
    </Box>
  );
};

Role.propTypes = {
  formHook: object
};

export default Role;
